import { useState, useRef } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'
import {
  Plus, Trash2, RefreshCw, Copy, ExternalLink, CheckCircle2,
  XCircle, Clock, Loader2, ArrowRightLeft, Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { toast } from 'sonner'
import {
  useTransferAccounts, useTransferOperations, useTransferTasks,
  usePairAccount, useUpdateTransferAccount, useDeleteTransferAccount,
  useCreateTransferTask, useDeleteTransferTask, useClearTransferTasks,
  useAvailableProfiles, useImportFromProfile, usePairFromCookies,
  type TransferAccount, type TransferTask,
} from '@/api/hooks/useTransfer'

function fmtRelative(ts: number | null) {
  if (!ts) return '—'
  const diff = Date.now() - ts
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s atrás`
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m atrás`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h atrás`
  return new Date(ts).toLocaleString('pt-BR')
}

const STATUS_BADGE: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string }> = {
  active: { variant: 'default', label: 'Ativa' },
  expired: { variant: 'destructive', label: 'Expirada' },
  error: { variant: 'destructive', label: 'Erro' },
  pending: { variant: 'outline', label: 'Pendente' },
  processing: { variant: 'secondary', label: 'Processando' },
  done: { variant: 'default', label: 'Concluída' },
  failed: { variant: 'destructive', label: 'Falhou' },
}

const OP_LABELS: Record<string, string> = {
  approval_check: 'Approval Check',
  star: 'Star',
  fan: 'Fan',
  reels: 'Reels',
  adbreak: 'Adbreak',
  cm: 'CM',
  bonus: 'Bonus',
  paid_event: 'Paid Event',
  rights_manager: 'Rights Manager',
  videograms: 'Videograms',
}

/* ─── Import Dialog (Cookies ou Perfis) ─── */
function ImportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useLanguage()
  const { data: profiles, isLoading } = useAvailableProfiles()
  const importProfile = useImportFromProfile()
  const pairCookies = usePairFromCookies()
  const [tab, setTab] = useState<'cookies' | 'profiles'>('cookies')
  const cookiesRef = useRef('')
  const [cookieLen, setCookieLen] = useState(0)
  const [name, setName] = useState('')
  const [fbDtsg, setFbDtsg] = useState('')

  const hasProfiles = (profiles?.length ?? 0) > 0

  const handleImport = (profileId: string, profileName: string) => {
    importProfile.mutate({ profile_id: profileId, name: profileName }, {
      onSuccess: (data) => {
        toast.success(data.updated ? `Sessão atualizada!` : `Perfil importado!`)
        onOpenChange(false)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  const handleCookiePair = () => {
    const val = cookiesRef.current.trim()
    if (!val) {
      toast.error('Cole os cookies do Facebook')
      return
    }
    pairCookies.mutate({
      cookies: val,
      name: name.trim() || undefined,
      fb_dtsg: fbDtsg.trim() || undefined,
    }, {
      onSuccess: (data) => {
        toast.success(data.updated ? 'Sessão atualizada!' : 'Conta adicionada!')
        cookiesRef.current = ''
        setCookieLen(0)
        setName('')
        setFbDtsg('')
        onOpenChange(false)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Adicionar Conta')}</DialogTitle>
          <DialogDescription>
            Cole os cookies do Facebook ou importe de um perfil conectado.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          <button
            onClick={() => setTab('cookies')}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === 'cookies' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Colar Cookies
          </button>
          <button
            onClick={() => setTab('profiles')}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === 'profiles' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Perfis Blissf {hasProfiles && `(${profiles!.length})`}
          </button>
        </div>

        {tab === 'cookies' ? (
          <div className="space-y-4 py-2">
            <div>
              <Label>{t('Nome (opcional)')}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Página Principal" />
            </div>
            <div>
              <Label>{t('Cookies do Facebook')}</Label>
              <textarea
                defaultValue=""
                onChange={(e) => {
                  cookiesRef.current = e.target.value
                  setCookieLen(e.target.value.length)
                }}
                placeholder={'Cole aqui os cookies. Aceita:\n• String HTTP (c_user=123; xs=abc; ...)\n• JSON do EditThisCookie ([{"name":"c_user","value":"123"}, ...])'}
                rows={8}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              {cookieLen > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">{cookieLen.toLocaleString()} caracteres colados</p>
              )}
              <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
                <strong>Como pegar:</strong> Abra facebook.com → F12 → Application → Cookies → copie todos.
                Ou instale a extensão "EditThisCookie" e exporte como JSON.
              </p>
            </div>
            <div>
              <Label>fb_dtsg (token CSRF)</Label>
              <Input
                value={fbDtsg}
                onChange={(e) => setFbDtsg(e.target.value)}
                placeholder="Ex: AQHkl..."
                className="font-mono text-xs"
              />
              <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
                <strong>Como pegar:</strong> No Facebook, Ctrl+U (ver código-fonte) → Ctrl+F → procure por <code>"DTSGInitialData"</code> → copie o valor do <code>"token"</code> logo após.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={handleCookiePair} disabled={pairCookies.isPending}>
                {pairCookies.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Conectar
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-2 py-2 max-h-[350px] overflow-auto">
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : !hasProfiles ? (
              <div className="p-6 text-center text-sm">
                <p className="text-muted-foreground">Nenhum perfil com cookies encontrado.</p>
                <p className="text-muted-foreground mt-1">Use a aba "Colar Cookies" ou conecte um perfil na seção Extensão.</p>
              </div>
            ) : (
              profiles!.map(profile => (
                <div
                  key={profile.id}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-secondary/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{profile.name}</span>
                      <Badge variant={profile.is_online ? 'default' : 'secondary'} className="text-[10px] px-1.5 py-0">
                        {profile.is_online ? 'Online' : 'Offline'}
                      </Badge>
                    </div>
                    {profile.fb_user_id && (
                      <span className="text-xs text-muted-foreground font-mono">ID: {profile.fb_user_id}</span>
                    )}
                    {profile.pages.length > 0 && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {profile.pages.length} página{profile.pages.length > 1 ? 's' : ''}: {profile.pages.map(p => p.name).join(', ')}
                      </div>
                    )}
                  </div>
                  <Button size="sm" onClick={() => handleImport(profile.id, profile.name)} disabled={importProfile.isPending}>
                    {importProfile.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Importar'}
                  </Button>
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/* ─── Pair Account Dialog ─── */
function PairDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useLanguage()
  const pair = usePairAccount()
  const [curl, setCurl] = useState('')
  const [name, setName] = useState('')

  const handleSubmit = () => {
    if (!curl.trim()) {
      toast.error('Cole o cURL capturado')
      return
    }
    pair.mutate({ curl: curl.trim(), name: name.trim() || undefined }, {
      onSuccess: () => {
        toast.success('Conta pareada com sucesso!')
        setCurl('')
        setName('')
        onOpenChange(false)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Parear Conta Facebook')}</DialogTitle>
          <DialogDescription>
            Cole o cURL capturado de um POST para facebook.com/api/graphql/
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>{t('Nome (opcional)')}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Página Principal"
            />
          </div>
          <div>
            <Label>{t('cURL')}</Label>
            <Textarea
              value={curl}
              onChange={(e) => setCurl(e.target.value)}
              placeholder="curl 'https://www.facebook.com/api/graphql/' ..."
              rows={8}
              className="font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Cancelar')}</Button>
          <Button onClick={handleSubmit} disabled={pair.isPending}>
            {pair.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('Parear')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Create Task Dialog ─── */
function TaskDialog({
  open, onOpenChange, accounts,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  accounts: TransferAccount[]
}) {
  const { t } = useLanguage()
  const { data: operations } = useTransferOperations()
  const createTask = useCreateTransferTask()
  const [accountId, setAccountId] = useState('')
  const [operation, setOperation] = useState('')
  const [docId, setDocId] = useState('')
  const [pageId, setPageId] = useState('')

  const activeAccounts = accounts.filter(a => a.status === 'active')

  const handleSubmit = () => {
    if (!accountId || !operation) {
      toast.error('Selecione conta e operação')
      return
    }
    const params: Record<string, unknown> = {}
    if (docId.trim()) params.doc_id = docId.trim()
    if (pageId.trim()) params.page_id = pageId.trim()

    createTask.mutate({ account_id: accountId, operation, params }, {
      onSuccess: (data) => {
        if (data.status === 'done') {
          toast.success(`${OP_LABELS[operation] || operation} executado com sucesso!`)
        } else {
          toast.error(`Falhou: ${data.error_message || 'Erro desconhecido'}`)
        }
        setAccountId('')
        setOperation('')
        setDocId('')
        setPageId('')
        onOpenChange(false)
      },
      onError: (e) => toast.error(e.message),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Criar Task')}</DialogTitle>
          <DialogDescription>
            Selecione a operação e a conta Facebook para executar
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label>{t('Conta Facebook')}</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger><SelectValue placeholder="Selecionar conta..." /></SelectTrigger>
              <SelectContent>
                {activeAccounts.map(a => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name || `FB ${a.fb_user_id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {activeAccounts.length === 0 && (
              <p className="text-xs text-destructive mt-1">Nenhuma conta ativa. Pareie uma conta primeiro.</p>
            )}
          </div>

          <div>
            <Label>{t('Operação')}</Label>
            <Select value={operation} onValueChange={setOperation}>
              <SelectTrigger><SelectValue placeholder="Selecionar operação..." /></SelectTrigger>
              <SelectContent>
                {(operations || []).map(op => (
                  <SelectItem key={op.key} value={op.key}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{t('doc_id (capturar via DevTools)')}</Label>
            <Input
              value={docId}
              onChange={(e) => setDocId(e.target.value)}
              placeholder="Ex: 1234567890123456"
              className="font-mono text-xs"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Abra DevTools → Network → filtre pelo nome da operação → copie o doc_id do payload
            </p>
          </div>

          <div>
            <Label>{t('Page ID (opcional)')}</Label>
            <Input
              value={pageId}
              onChange={(e) => setPageId(e.target.value)}
              placeholder="Ex: 61586387228721"
              className="font-mono text-xs"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('Cancelar')}</Button>
          <Button onClick={handleSubmit} disabled={createTask.isPending}>
            {createTask.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('Executar')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Main Page ─── */
export function Transfer() {
  const { t } = useLanguage()
  const { data: accounts, isLoading: loadingAccounts } = useTransferAccounts()
  const { data: tasks, isLoading: loadingTasks } = useTransferTasks()
  const deleteAccount = useDeleteTransferAccount()
  const deleteTask = useDeleteTransferTask()
  const clearTasks = useClearTransferTasks()

  const [pairOpen, setPairOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [taskOpen, setTaskOpen] = useState(false)
  const [selectedTask, setSelectedTask] = useState<TransferTask | null>(null)

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            {t('Transfer Manager')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie contas Facebook e execute operações de monetização
          </p>
        </div>
      </div>

      {/* Accounts Section */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-sm font-semibold">{t('Contas Pareadas')}</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
              <RefreshCw className="mr-1 h-3 w-3" />
              {t('Importar do Blissf')}
            </Button>
            <Button size="sm" onClick={() => setPairOpen(true)}>
              <Plus className="mr-1 h-3 w-3" />
              {t('Parear via cURL')}
            </Button>
          </div>
        </div>

        {loadingAccounts ? (
          <div className="p-4 space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !accounts?.length ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Nenhuma conta pareada. Clique em "Parear Conta" para adicionar uma.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Nome')}</TableHead>
                <TableHead>{t('FB User ID')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
                <TableHead>{t('Tasks')}</TableHead>
                <TableHead>{t('Pareado')}</TableHead>
                <TableHead>{t('Último uso')}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map(account => {
                const badge = STATUS_BADGE[account.status] || STATUS_BADGE.error
                return (
                  <TableRow key={account.id}>
                    <TableCell className="font-medium">
                      {account.name || '—'}
                      {account.profile_url && (
                        <a
                          href={account.profile_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-1 inline-flex"
                        >
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{account.fb_user_id || '—'}</TableCell>
                    <TableCell><Badge variant={badge.variant}>{badge.label}</Badge></TableCell>
                    <TableCell>{account.tasks_count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelative(account.paired_at)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelative(account.last_used_at)}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => {
                          if (confirm('Remover esta conta?')) {
                            deleteAccount.mutate(account.id, {
                              onSuccess: () => toast.success('Conta removida'),
                              onError: (e) => toast.error(e.message),
                            })
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Tasks Section */}
      <div className="rounded-lg border bg-card">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-sm font-semibold">{t('Tasks')}</h2>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => clearTasks.mutate('done', {
                onSuccess: (d) => toast.success(`${d.deleted} tasks removidas`),
              })}
            >
              {t('Limpar concluídas')}
            </Button>
            <Button size="sm" onClick={() => setTaskOpen(true)} disabled={!accounts?.length}>
              <Zap className="mr-1 h-3 w-3" />
              {t('Nova Task')}
            </Button>
          </div>
        </div>

        {loadingTasks ? (
          <div className="p-4 space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : !tasks?.length ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            Nenhuma task criada ainda. Selecione uma conta e operação para começar.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('Operação')}</TableHead>
                <TableHead>{t('Conta')}</TableHead>
                <TableHead>{t('Status')}</TableHead>
                <TableHead>{t('Criado')}</TableHead>
                <TableHead>{t('Resultado')}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map(task => {
                const badge = STATUS_BADGE[task.status] || STATUS_BADGE.failed
                const StatusIcon = task.status === 'done' ? CheckCircle2
                  : task.status === 'failed' ? XCircle
                  : task.status === 'processing' ? Loader2
                  : Clock
                return (
                  <TableRow key={task.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`h-4 w-4 ${
                          task.status === 'done' ? 'text-green-500'
                          : task.status === 'failed' ? 'text-destructive'
                          : task.status === 'processing' ? 'animate-spin text-blue-500'
                          : 'text-muted-foreground'
                        }`} />
                        <span className="font-medium">{OP_LABELS[task.operation] || task.operation}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{task.account?.name || task.account?.fb_user_id || '—'}</TableCell>
                    <TableCell><Badge variant={badge.variant}>{badge.label}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelative(task.created_at)}</TableCell>
                    <TableCell className="max-w-[200px]">
                      {task.status === 'done' && task.result ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => setSelectedTask(task)}
                        >
                          Ver resultado
                        </Button>
                      ) : task.status === 'failed' ? (
                        <span className="text-xs text-destructive truncate block">
                          {task.error_message || 'Erro'}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => {
                          deleteTask.mutate(task.id, {
                            onSuccess: () => toast.success('Task removida'),
                          })
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Result Dialog */}
      <Dialog open={!!selectedTask} onOpenChange={() => setSelectedTask(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Resultado: {selectedTask ? (OP_LABELS[selectedTask.operation] || selectedTask.operation) : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-auto">
            <pre className="text-xs bg-muted p-3 rounded-lg whitespace-pre-wrap break-all font-mono">
              {selectedTask?.result ? JSON.stringify(selectedTask.result, null, 2) : 'Sem dados'}
            </pre>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (selectedTask?.result) {
                  navigator.clipboard.writeText(JSON.stringify(selectedTask.result, null, 2))
                  toast.success('Copiado!')
                }
              }}
            >
              <Copy className="mr-1 h-3 w-3" />
              Copiar JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <PairDialog open={pairOpen} onOpenChange={setPairOpen} />
      <TaskDialog open={taskOpen} onOpenChange={setTaskOpen} accounts={accounts || []} />
    </div>
  )
}
